import { ActionTree } from 'vuex';
import router from '../../../router/index';
import ApiUrl from '../../../api/url';
import { NodeIdpState, NewNodeIdp, EditNodeIdp, TestNodeIdp } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import { isHappenedOtherPartyError } from '../../error-type';
import ScreenUrl from '../../../router/url';

export const actions: ActionTree<NodeIdpState, RootState> = {
    /**
     * ノードIdP一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { pageNumber, perPage, sort } = {}) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.INDEX_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.NodeIdp.INDEX, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.INDEX_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.INDEX_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ノードIdP新規作成前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async new({ commit }) {
        commit(types.NEW_NODE_IDP);
    },

    /**
     * ノードIdP新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewNodeIdp} newNodeIdp 新規ノードIdP情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newNodeIdp: NewNodeIdp) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.NodeIdp.CREATE,
                    params: newNodeIdp,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.NodeIdp.CREATED);
                dispatch('toastMessage', `${newNodeIdp.name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * ノードIdPの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id ノードIdPID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.NodeIdp.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ノードIdP更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {EditNodeIdp} editNodeIdp 更新するノードIdP情報
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, editNodeIdp: EditNodeIdp) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.NodeIdp.UPDATE.replace(':id', editNodeIdp.id.toString()),
                    params: editNodeIdp,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.NodeIdp.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * パラメーターのIdP情報で接続テスト
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} name ノードIdP名称
     * @param {TestNodeIdp} testNodeIdp 接続先IdP情報
     * @returns {Promise<void>}
     */
    async connection({ dispatch, commit, rootState }, { name, testNodeIdp }) {
        try {
            commit(types.CONNECTION_TEST_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.NodeIdp.CONNECTION,
                    params: testNodeIdp,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CONNECTION_TEST_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.NodeIdp.CONNECTED);
                dispatch('toastMessage', `${name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CONNECTION_TEST_FAILURE, params);
        }
    },

    /**
     * ノードIdP削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id ノードIdPのID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.NodeIdp.DESTROY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.NodeIdp.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { sort } = {}) {
        try {
            const payload = {
                sort: sort,
            };
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.NodeIdp.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS, resData);
                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CSV_OUTPUT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
